import type { SpeechRequest } from '@/api/types';
import type { RpcCallOptions, RpcService } from './rpcTypes';

export const MAX_SHARED_SPEECH_BYTES = 5 * 1024 * 1024;
const CHUNK_BYTES = 32 * 1024;
const MAX_CLOCK_RTT_MS = 2000;

export type SpeechTarget = {
  call<T = unknown>(service: RpcService, data?: unknown, options?: RpcCallOptions): Promise<T>;
};

export class SharedSpeechCache {
  private readonly audio = new Map<string, ArrayBuffer>();
  private bytes = 0;

  async get(
    request: SpeechRequest,
    synthesize: (request: SpeechRequest, signal?: AbortSignal) => Promise<ArrayBuffer>,
    signal?: AbortSignal,
  ) {
    signal?.throwIfAborted();
    const key = JSON.stringify([request.text, request.speaker ?? 3, request.speedScale ?? 1, request.pitchScale ?? 0]);
    const cached = this.audio.get(key);
    if (cached) return cached;
    const audio = await synthesize(request, signal);
    signal?.throwIfAborted();
    if (audio.byteLength === 0 || audio.byteLength > MAX_SHARED_SPEECH_BYTES) {
      throw new Error('同期発話の音声サイズが範囲外です（1バイト〜5MiB）');
    }
    while (this.bytes + audio.byteLength > MAX_SHARED_SPEECH_BYTES) {
      const oldest = this.audio.entries().next().value;
      if (!oldest) break;
      this.audio.delete(oldest[0]);
      this.bytes -= oldest[1].byteLength;
    }
    this.audio.set(key, audio);
    this.bytes += audio.byteLength;
    return audio;
  }
}

export async function playSynchronizedSpeech(
  targets: readonly SpeechTarget[],
  audio: ArrayBuffer,
  options: RpcCallOptions,
  introduction?: {
    motionId?: number;
    onStarted?: () => void;
    onCompleted?: (part: 'audio' | 'motion') => void;
  },
): Promise<void> {
  if (targets.length === 0) return;
  if (audio.byteLength === 0 || audio.byteLength > MAX_SHARED_SPEECH_BYTES) {
    throw new Error('同期発話の音声サイズが範囲外です');
  }
  const transferId = crypto.randomUUID();
  const bytes = new Uint8Array(audio);
  const digest = await crypto.subtle.digest('SHA-256', audio);
  const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
  const controlOptions = { ...options, timeoutMs: 10_000 };
  const controller = new AbortController();
  const abort = () => controller.abort(options.signal?.reason);
  options.signal?.addEventListener('abort', abort, { once: true });
  if (options.signal?.aborted) abort();
  const transferOptions = { ...controlOptions, signal: controller.signal };

  async function call<T>(target: SpeechTarget, service: RpcService, data?: unknown): Promise<T> {
    controller.signal.throwIfAborted();
    return target.call<T>(service, data, transferOptions);
  }

  try {
    await Promise.all(targets.map(async (target) => {
      await call(target, 'beginSpeechAudio', { transferId, byteLength: bytes.length, sha256 });
      for (let offset = 0; offset < bytes.length; offset += CHUNK_BYTES) {
        const chunk = bytes.subarray(offset, offset + CHUNK_BYTES);
        const base64 = btoa(String.fromCharCode(...chunk));
        await call(target, 'appendSpeechAudio', { transferId, offset, base64 });
      }
      await call(target, 'prepareSpeechAudio', { transferId });
    }));

    const clocks = await Promise.all(targets.map(async (target) => {
      let best: { offset: number; rtt: number } | undefined;
      for (let sample = 0; sample < 3; sample += 1) {
        const sentAt = performance.now();
        const robotTime = await call<unknown>(target, 'speechClock');
        const receivedAt = performance.now();
        if (typeof robotTime !== 'number' || !Number.isFinite(robotTime) || robotTime < 0) {
          throw new Error('ロボットの同期時計応答が不正です。Androidアプリを更新してください');
        }
        const rtt = receivedAt - sentAt;
        if (!best || rtt < best.rtt) best = { offset: robotTime - (sentAt + receivedAt) / 2, rtt };
      }
      if (!best || best.rtt > MAX_CLOCK_RTT_MS) throw new Error('通信遅延が大きいため同期発話できません');
      return best;
    }));
    const minimumLeadMs = introduction && targets.length === 1 ? 250 : 1000;
    const leadMs = Math.max(minimumLeadMs, ...clocks.map(({ rtt }) => rtt * 4 + 250));
    const startAt = performance.now() + leadMs;
    await Promise.all(targets.map((target, index) => call(target, 'scheduleSpeechAudio', {
      transferId,
      startAtMs: Math.round(startAt + clocks[index].offset),
      ...(introduction ? { motionId: introduction.motionId } : {}),
        ...(introduction?.motionId !== undefined ? { motionId: introduction.motionId } : {}),
    })));
    // Never let late acknowledgements turn a missed barrier into apparently synchronized playback.
    if (performance.now() >= startAt - 100) throw new Error('同期発話の予約確認が開始時刻に間に合いませんでした');
    if (introduction) {
      await Promise.all(targets.map((target) => target.call('awaitSpeechAudio', { transferId, part: 'started' }, options)));
      introduction.onStarted?.();
      await Promise.allSettled((['audio', 'motion'] as const).map(async (part) => {
        const results = await Promise.allSettled(targets.map((target) => target.call('awaitSpeechAudio', { transferId, part }, options)));
        if (results.every((result) => result.status === 'fulfilled')) introduction.onCompleted?.(part);
      }));
      const completions = await Promise.allSettled(targets.map((target) => target.call('awaitSpeechAudio', { transferId }, options)));
      const failure = completions.find((result) => result.status === 'rejected');
      if (failure?.status === 'rejected') throw failure.reason;
      return;
    }
    await Promise.all(targets.map((target) => target.call('awaitSpeechAudio', { transferId }, {
      ...options,
      signal: controller.signal,
    })));
  } catch (error) {
    controller.abort();
    const cleanup = await Promise.allSettled(targets.map((target) => target.call('stopSpeak', undefined, {
      ...controlOptions,
      signal: undefined,
    })));
    const failures = cleanup.flatMap((result) => result.status === 'rejected' ? [result.reason] : []);
    if (failures.length > 0) {
      throw new AggregateError([error, ...failures], '同期発話に失敗し、一部のロボットの停止も確認できませんでした');
    }
    throw error;
  } finally {
    options.signal?.removeEventListener('abort', abort);
  }
}
